import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Briefcase, Award, Users, Building2, Sparkles, Monitor, TrendingUp, CheckCircle2 } from 'lucide-react';

export default function BigScreenView() {
    const [data, setData] = useState(null);

    const fetchFeed = async () => {
        try {
            const res = await axios.get('/api/dashboard/big-screen');
            if (res.data.success) setData(res.data.big_screen);
        } catch (err) {
            console.error('Big screen feed error');
        }
    };

    useEffect(() => {
        fetchFeed();
        const interval = setInterval(fetchFeed, 8000); // Auto refresh every 8 seconds
        return () => clearInterval(interval);
    }, []);

    const leaderboard = data?.leaderboard || [];
    const recentSelections = data?.recent_selections || [];

    return (
        <div className="min-h-screen bg-slate-950 text-white p-6 md:p-10 relative overflow-hidden flex flex-col justify-between selection:bg-indigo-500">
            
            {/* Background Ambient Glows */}
            <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-indigo-600/15 rounded-full blur-[120px] pointer-events-none animate-pulse-glow"></div>
            <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-purple-600/15 rounded-full blur-[120px] pointer-events-none"></div>

            {/* Top Bar Header */}
            <div className="flex items-center justify-between border-b border-slate-800/80 pb-6">
                <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-indigo-600 via-purple-600 to-pink-500 p-0.5 shadow-2xl shadow-indigo-500/30">
                        <div className="w-full h-full bg-slate-950 rounded-[14px] flex items-center justify-center">
                            <Briefcase className="w-7 h-7 text-indigo-400" />
                        </div>
                    </div>
                    <div>
                        <h1 className="text-4xl font-black tracking-tight bg-gradient-to-r from-white via-indigo-100 to-indigo-400 bg-clip-text text-transparent">
                            Avsar (अवसर) Mega Job Fair 2026
                        </h1>
                        <p className="text-sm font-semibold text-slate-400 tracking-wider uppercase mt-0.5">
                            Live Hiring Feed • Event Venue Presentation Board
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    <span className="flex items-center space-x-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-2 rounded-full font-black text-sm glow-emerald">
                        <span className="w-3 h-3 rounded-full bg-emerald-500 animate-ping"></span>
                        <span>LIVE BROADCAST</span>
                    </span>
                </div>
            </div>

            {/* Main Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 my-8">
                <div className="glass-card rounded-3xl p-6 border border-slate-800 text-center relative overflow-hidden group">
                    <div className="text-slate-400 font-bold uppercase tracking-wider text-xs">Total Candidates</div>
                    <div className="text-6xl font-black text-white mt-3">{data?.total_applicants || 0}</div>
                    <div className="text-xs text-indigo-300 font-semibold mt-2">Registered Job Seekers</div>
                </div>

                <div className="glass-card rounded-3xl p-6 border border-slate-800 text-center relative overflow-hidden group">
                    <div className="text-slate-400 font-bold uppercase tracking-wider text-xs">Active Companies</div>
                    <div className="text-6xl font-black text-purple-400 mt-3">{data?.total_companies || 0}</div>
                    <div className="text-xs text-purple-300 font-semibold mt-2">Employer Stalls</div>
                </div>

                <div className="glass-card rounded-3xl p-6 border border-emerald-500/30 text-center relative overflow-hidden glow-emerald">
                    <div className="text-emerald-400 font-extrabold uppercase tracking-wider text-xs flex items-center justify-center space-x-1">
                        <Award className="w-4 h-4 text-emerald-400" />
                        <span>Shortlisted Candidates</span>
                    </div>
                    <div className="text-6xl font-black text-emerald-400 mt-3">{data?.total_selected || 0}</div>
                    <div className="text-xs text-emerald-300 font-semibold mt-2">Hired / Selected Today</div>
                </div>

                <div className="glass-card rounded-3xl p-6 border border-slate-800 text-center relative overflow-hidden group">
                    <div className="text-slate-400 font-bold uppercase tracking-wider text-xs">Interviews Conducted</div>
                    <div className="text-6xl font-black text-cyan-300 mt-3">{data?.total_interviews || 0}</div>
                    <div className="text-xs text-cyan-300 font-semibold mt-2">Total Stall Interviews</div>
                </div>
            </div>

            {/* Leaderboard Cards */}
            <div className="mb-8">
                <div className="flex items-center space-x-2 mb-4">
                    <Sparkles className="w-6 h-6 text-amber-400" />
                    <h2 className="text-2xl font-extrabold text-white">Top Hiring Companies Today</h2>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {leaderboard.map((comp, idx) => (
                        <div key={comp.id} className="glass-card rounded-2xl p-5 border border-slate-800 flex items-center justify-between hover:border-slate-700 transition-all">
                            <div>
                                <span className="text-[10px] font-black text-amber-400 bg-amber-500/20 px-2 py-0.5 rounded">
                                    RANK #{idx + 1}
                                </span>
                                <div className="font-extrabold text-lg text-white mt-1 truncate max-w-[140px]">{comp.company_name}</div>
                                <div className="text-xs text-slate-400">{comp.office_location || 'Indore'}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-3xl font-black text-emerald-400">{comp.selected_count || 0}</div>
                                <div className="text-[10px] text-slate-400 uppercase font-bold">Shortlisted</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Bottom Live Selection Ticker */}
            <div className="glass-card rounded-2xl p-4 border border-indigo-500/30 overflow-hidden bg-slate-900/90">
                <div className="flex items-center space-x-3 mb-2 text-xs font-bold text-indigo-300 uppercase tracking-widest">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 animate-bounce" />
                    <span>Recent Live Candidate Selections Announcement</span>
                </div>

                <div className="flex space-x-6 overflow-x-auto py-2 scrollbar-none">
                    {recentSelections.map((sel) => (
                        <div key={sel.id} className="shrink-0 bg-slate-950 px-4 py-2.5 rounded-xl border border-slate-800 flex items-center space-x-3">
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
                            <div>
                                <span className="font-bold text-sm text-white">{sel.applicant?.name}</span>
                                <span className="text-xs text-slate-400 ml-2">selected by <strong className="text-amber-300">{sel.company?.company_name}</strong></span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

        </div>
    );
}
